import { Component, QueryList, inject, signal, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';
import { DataFieldGroupContainerComponent } from '../shared/datafields/datafield-group-container.component';
import { DataFieldGroup } from '../shared/datafields/models/datafield-group';
import { DataFieldValue } from '../shared/datafields/models/datafield-value';
import { LoadingPanelService } from '../shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from '../shared/toast/toast.service';
import { PartnerConfigurationKeyService } from './services/partner-configuration-key.service';
import { PartnerComponentBase } from './partner-search-details.base';
import { UntypedFormGroup } from '@angular/forms';

@Component({
  templateUrl: './partner-search-details-configuration-keys.component.html',
  selector: 'ep-partner-details-configuration-keys',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DataFieldGroupContainerComponent, PermissionRequiredDirective]
})
export class PartnerSearchDetailsConfigurationKeysComponent extends PartnerComponentBase {
  private readonly keyUsageGeneral = 'Partner_General';
  private readonly keyUsageSpecific = 'Partner_Specific';

  private readonly partnerConfigurationKeyService = inject(PartnerConfigurationKeyService);
  private readonly toastService = inject(ToastService);
  private readonly loadingPanelService = inject(LoadingPanelService);
  readonly isEditing = signal(false);
  readonly generalConfigurations = signal<DataFieldGroup[]>([]);
  readonly specificConfigurations = signal<DataFieldGroup[]>([]);
  readonly validationFormGroup: UntypedFormGroup = new UntypedFormGroup({});

  @ViewChildren(DataFieldGroupContainerComponent) datafieldGroupContainers: QueryList<DataFieldGroupContainerComponent>;

  async partnerSet(): Promise<void> {
    const [gc, sc] = await Promise.all([
      this.findConfigurationKeys(this.keyUsageGeneral, 'General'),
      this.findConfigurationKeys(this.keyUsageSpecific, 'Specific', this.selectedPartner?.PartnersId),
    ]);

    this.generalConfigurations.set(gc);
    this.specificConfigurations.set(sc);
  }

  async findConfigurationKeys(keyUsageName: string, groupName: string, foreignKey?: number): Promise<DataFieldGroup[]> {
    let result: DataFieldGroup[] = [];
    if (this.selectedPartner?.PartnersId) {
      const keys = await this.partnerConfigurationKeyService.getConfigurationKeys(this.selectedPartner.PartnersId, keyUsageName, foreignKey);
      if (keys?.length > 0) {
        result = [new DataFieldGroup(undefined, groupName, undefined, undefined, keys)];
      }
    }
    return result;
  }

  edit(): void {
    this.isEditing.set(true);
    this.setEditMode(true);
    this.datafieldGroupContainers.forEach(group => group.edit());
  }

  cancel(): void {
    this.isEditing.set(false);
    this.datafieldGroupContainers.forEach(group => group.cancel());
    this.setEditMode(false);
  }

  async save(): Promise<void> {
    this.datafieldGroupContainers.forEach(group => group.commit());
    this.loadingPanelService.showLoadingPanelDirect('Saving...');
    try {
      // Collect all datafields in both groups
      const allDataFields: DataFieldValue[] = this.datafieldGroupContainers.toArray().flatMap(container => container.getDataFieldValues(true));
      const partnerId = this.selectedPartner?.PartnersId;

      if (partnerId) {
        await this.partnerConfigurationKeyService.saveConfigurationKeys(partnerId, allDataFields);
        // Refresh to ensure that password fields read updated secretId's.
        await this.partnerSet();
        this.isEditing.set(false);
        this.setEditMode(false);
        this.toastService.showToast('Configuration keys saved...', ToastType.success);
      }
    }
    finally {
      this.loadingPanelService.hideLoadingPanel();
    }
  }
}
